"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";

export default function Slides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/slides`,
      );
      setSlides(response.data);
    } catch (error) {
      console.error("Error fetching slides:", error);
      setMessage("Failed to load slides.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination || destination.index === source.index) return;

    const reorderedSlides = Array.from(slides);
    const [movedSlide] = reorderedSlides.splice(source.index, 1);
    reorderedSlides.splice(destination.index, 0, movedSlide);

    const updatedSlides = reorderedSlides.map((slide, index) => ({
      ...slide,
      position: index + 1,
    }));

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/slides/reorder`, {
        slides: updatedSlides.map(({ id, position }) => ({ id, position })),
      });

      setSlides(updatedSlides);
      setMessage("Slides reordered successfully.");
    } catch (error) {
      console.error("Error updating order:", error);
      setMessage("Failed to reorder slides.");
    }
  };

  const handleDelete = async (slideId) => {
    if (window.confirm("Are you sure you want to delete this slide?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/slides/${slideId}`,
        );
        setSlides(slides.filter((slide) => slide.id !== slideId));
        setMessage("Slide deleted successfully.");
      } catch (error) {
        console.error("Error deleting slide:", error);
        setMessage("Failed to delete slide.");
      }
    }
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-start mb-4">
        <h3 className="mb-0 me-3">Slides</h3>

        <Link
          to="/xl-admin/add-slides"
          className="btn btn-inchworm btn-sm"
          role="button"
          style={{
            padding: "0.25rem 0.4rem",
            fontSize: "0.8rem",
            lineHeight: "1",
          }}
        >
          Add New
        </Link>
      </div>

      {/* Message */}
      {message && <div className="alert alert-info">{message}</div>}

      {/* Slides Table */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="slides" direction="vertical">
            {(provided) => (
              <div
                className="table-responsive"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <table className="table table-bordered table-striped">
                  <thead className="table-success">
                    <tr>
                      <th>#</th>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slides.length > 0 ? (
                      slides.map((slide, index) => (
                        <Draggable
                          key={slide.id}
                          draggableId={String(slide.id)}
                          index={index}
                        >
                          {(provided) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                cursor: "grab",
                                ...provided.draggableProps.style,
                              }}
                            >
                              <td>{index + 1}</td>
                              <td>
                                <img
                                  src={slide.background_image}
                                  alt="Slide"
                                  style={{
                                    width: "96px",
                                    height: "64px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                  }}
                                />
                              </td>
                              <td>{slide.title || "No Title"}</td>
                              <td>{slide.slide_type}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Link
                                    to={`/xl-admin/edit-slide?id=${slide.id}`}
                                    className="btn btn-sm btn-warning"
                                  >
                                    Edit
                                  </Link>
                                  <button
                                    onClick={() => handleDelete(slide.id)}
                                    className="btn btn-danger btn-sm"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">No slides found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
